import React, { useState, useEffect, useRef } from 'react';
import { Send, Mail, MapPin, Phone, CheckCircle, MessageSquare, Github, Linkedin } from 'lucide-react';
import { portfolioData } from '../data/mock';

const Contact = () => {
  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const res = await fetch('https://included-swift-helpful.ngrok-free.app/send-contact/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formData.name, mail_to: formData.email, subject: formData.subject, message: formData.message })
      });
      if (res.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setIsSubmitted(false), 5000);
      } else setError('Failed to send. Please try again.');
    } catch {
      setError('Mail server being configured. Reach me at ranjithkumar98.sekar@gmail.com');
    } finally { setIsSubmitting(false); }
  };

  return (
    <section id="contact" ref={sectionRef} className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 grid-bg" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`transition-all duration-1000 transform ${visible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-mono mb-4">
              <MessageSquare size={12} />
              <span>Get in Touch</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Let's Build Something <span className="gradient-text">Intelligent</span>
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">Whether it's AI, automation, backend systems or infrastructure — I'm always interested in solving meaningful engineering challenges.</p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8 sm:gap-12 max-w-5xl mx-auto">
            <div className="lg:col-span-2 space-y-6">
              <div className="glass rounded-2xl p-6 space-y-6">
                {[
                  { icon: Mail, color: 'from-indigo-500/20 to-purple-500/20', textColor: 'text-indigo-400', label: 'Email', value: portfolioData.personal.email, href: `mailto:${portfolioData.personal.email}` },
                  { icon: Phone, color: 'from-cyan-500/20 to-blue-500/20', textColor: 'text-cyan-400', label: 'Phone', value: portfolioData.personal.phone },
                  { icon: MapPin, color: 'from-amber-500/20 to-orange-500/20', textColor: 'text-amber-400', label: 'Location', value: portfolioData.personal.location }
                ].map((item) => (
                  <div key={item.label} className="flex items-start space-x-4">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center flex-shrink-0`}>
                      <item.icon className={item.textColor} size={18} />
                    </div>
                    <div>
                      <div className="text-white text-sm font-medium">{item.label}</div>
                      {item.href ? <a href={item.href} className="text-gray-500 text-sm hover:text-indigo-400 transition-colors">{item.value}</a>
                        : <span className="text-gray-500 text-sm">{item.value}</span>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center space-x-3">
                <a href={portfolioData.personal.github} target="_blank" rel="noopener noreferrer" className="p-3 glass rounded-xl text-gray-400 hover:text-indigo-400 hover:border-indigo-500/30 transition-all duration-300 hover:scale-110"><Github size={18} /></a>
                <a href={portfolioData.personal.linkedin} target="_blank" rel="noopener noreferrer" className="p-3 glass rounded-xl text-gray-400 hover:text-indigo-400 hover:border-indigo-500/30 transition-all duration-300 hover:scale-110"><Linkedin size={18} /></a>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="glass rounded-3xl p-6 sm:p-8 border border-white/5">
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4"><CheckCircle className="text-green-400" size={32} /></div>
                    <h3 className="text-white text-lg font-semibold mb-2">Message Sent!</h3>
                    <p className="text-gray-500 text-sm">Thank you. I'll get back to you within 24 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-gray-400 text-sm font-medium mb-2">Name</label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="John Doe"
                          className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500/50 focus:bg-white/[0.05] transition-all duration-300 text-sm" />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm font-medium mb-2">Email</label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="john@example.com"
                          className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500/50 focus:bg-white/[0.05] transition-all duration-300 text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm font-medium mb-2">Subject</label>
                      <input type="text" name="subject" value={formData.subject} onChange={handleChange} required placeholder="What's this about?"
                        className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500/50 focus:bg-white/[0.05] transition-all duration-300 text-sm" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm font-medium mb-2">Message</label>
                      <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} placeholder="Tell me about your project or idea..."
                        className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500/50 focus:bg-white/[0.05] transition-all duration-300 text-sm resize-none" />
                    </div>
                    {error && <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-sm">{error}</div>}
                    <button type="submit" disabled={isSubmitting}
                      className="w-full px-6 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:shadow-lg hover:shadow-indigo-500/25 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2">
                      {isSubmitting ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" /> : <Send size={16} />}
                      <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
